/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { LogActionId } from "@smartsheet/logging-state-service";
import { PropsWithChildren, useMemo, ContextType } from "react";
import { SelectComponentsConfig, ValueType } from "react-select";
import { LoggableEvent, LoggedByDefault, useLoggingDecorator, useLoggingEnabledByDefault } from "../../util/logging";
import { SelectBase, SelectBaseProps } from "./SelectBase";
import { LayoutContextProvider } from "./components/LayoutContext";
import { DEFAULT_ALL_OPTION_VALUE, MultiSelectOption } from "./components/MultiSelectOption";
import { MultiValueContainer } from "./components/MultiValueContainer";
import { SelectAllContext } from "./components/SelectAllContext";
import { DEFAULT_ITEM_HEIGHT, MAX_OPTIONS_UNTIL_VIRTUALIZATION } from "./selectConstants";
import { OptionType, OptionsType, ActionMeta, CommonSelectProps, MultiSelectDefaultComponentsConfig } from "./types";

const getComponents = <SelectOption extends OptionType>(
    components: Partial<MultiSelectDefaultComponentsConfig<SelectOption>> = {}
): SelectComponentsConfig<SelectOption> => {
    return {
        Option: components.MultiSelectOption || MultiSelectOption,
        ValueContainer: components.MultiValueContainer || MultiValueContainer,
        ...components,
    };
};

export type MultiSelectValueType<SelectOption extends OptionType> = OptionsType<SelectOption> | undefined;

export interface MultiSelectProps<SelectOption extends OptionType> extends CommonSelectProps<SelectOption> {
    /** Callback invoked whenever a change of selection is made. Use this to change the data backing the `value` prop */
    onChange: (
        value: MultiSelectValueType<SelectOption>,
        action: ActionMeta<SelectOption>,
        log: LoggableEvent<LoggedByDefault>
    ) => void;
    // Options was pulled out of CommonSelectProps for docs purposes
    /** Array of options that populate the select menu */
    options: OptionsType<SelectOption>;
    /** The array of currently selected options */
    value: MultiSelectValueType<SelectOption>;
    /**
     * The configuration for the "select all" option. Defaults to false meaning "don't include the option".
     * If present, see the following documentation on this prop's shape:
     *
     * - getText<span style="color: red;">*</span>: A function to use for the text of the "Select All" item. This should be internationalized.
     */
    selectAllConfig?:
        | false
        | {
              getText: (selectedCount: number, totalCount: number) => string;
          };
}

const hasSelectAllConfig = (config: unknown): config is Record<string, unknown> =>
    config !== null && typeof config === "object";

/**
 * Wraps the SelectBase component with correct props for a "Multi" select (versus the blend of
 * Single- and Multi- select props that SelectBase takes).
 *
 * Also provides some default implementations for multi-select behavior (such as a select-all
 * option and checkboxes next to options).
 *
 * @deprecated This component is being removed in V6+, February 2025, please use `MultiSelectV2` instead.
 */
export const MultiSelect = <SelectOption extends OptionType>({
    validationState = "default",
    closeMenuOnSelect = false,
    maxOptionsUntilVirtualization = MAX_OPTIONS_UNTIL_VIRTUALIZATION,
    optionHeight = DEFAULT_ITEM_HEIGHT,
    closeMenuOnScroll = false,
    createOptionPosition = "last",
    isClearable = true,
    isSearchable = true,
    menuPortalTarget = document.body,
    menuPlacement = "auto",
    selectAllConfig = false,
    components,
    ...restProps
}: PropsWithChildren<MultiSelectProps<SelectOption>>) => {
    const {
        // Setting defaults on these outside of props destructuring to keep them marked as required for mdx
        value = [],
        options = [],
        onChange = () => {},
    } = restProps;

    const withLogging = useLoggingDecorator({
        controlType: "mlc",
        clientId: restProps.clientId,
    });
    const loggableChangeHandler = useLoggingEnabledByDefault(
        withLogging,
        {
            actionId: LogActionId.CLICK,
        },
        onChange
    );

    const handleChange = (selected: MultiSelectValueType<SelectOption>, actionMeta: ActionMeta<SelectOption>) => {
        if (
            selected &&
            selected.length > 0 &&
            // Might seem weird since select all is the first option in the list, but the selected items are returned
            // in the order they're selected in.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
            (selected[selected.length - 1] as any).value === DEFAULT_ALL_OPTION_VALUE
        ) {
            // Select All option was selected
            if (value?.length === options.length) {
                // Every option is selected already, so we should un-select everything
                return loggableChangeHandler([], {
                    action: "clear",
                });
            } else {
                // Select all the options
                return loggableChangeHandler(options, actionMeta);
            }
        }
        return loggableChangeHandler(selected, actionMeta);
    };

    const hasSelectAll = hasSelectAllConfig(selectAllConfig);

    const memoizedOptions = useMemo(() => {
        const optionsCopy = [
            ...(hasSelectAll
                ? [
                      {
                          label: "",
                          value: DEFAULT_ALL_OPTION_VALUE,
                      } as never,
                  ]
                : []),
            ...options,
        ];

        return optionsCopy;
    }, [options, hasSelectAll]);

    let newProps: SelectBaseProps<SelectOption> = {
        // Consumer can override these props.
        closeMenuOnSelect,
        createOptionPosition,
        validationState,
        maxOptionsUntilVirtualization,
        closeMenuOnScroll,
        isClearable,
        isSearchable,
        optionHeight,
        menuPortalTarget,
        menuPlacement,

        ...restProps,

        // These *must* be after the restProps spread, since we don't want to let the consumer override them
        options: memoizedOptions,
        value: value as ValueType<SelectOption>,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onChange: (value, action) => loggableChangeHandler(value as MultiSelectValueType<SelectOption>, action),
        components: getComponents(
            // Once we re-align the components interfaces this cast won't be necessary :(
            // See https://git.lab.smartsheet.com/design-systems-team/lodestar-core/-/issues/249
            components as MultiSelectDefaultComponentsConfig<SelectOption>
        ),
        hideSelectedOptions: false,
        isMulti: true,
    };

    if (hasSelectAll) {
        // Amend new props to turn on Select All
        newProps = {
            ...newProps,
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onChange: (value, actionMeta) => handleChange(value as MultiSelectValueType<SelectOption>, actionMeta),
        };
    }

    const getText = selectAllConfig && selectAllConfig.getText;
    const selectAllContextValue: ContextType<typeof SelectAllContext> = useMemo(
        () => ({
            enabled: hasSelectAll,
            getText: getText || (() => ""),
        }),
        [hasSelectAll, getText]
    );

    return (
        <LayoutContextProvider>
            <SelectAllContext.Provider value={selectAllContextValue}>
                <SelectBase {...newProps} />
            </SelectAllContext.Provider>
        </LayoutContextProvider>
    );
};
